/* eslint-disable @typescript-eslint/naming-convention */
/**
 * This module encompasses reused browser-detection logic.
 *
 * The user agent parsing happens at module loading time, and populates the
 * Browser object with the matching browser types as properties.
 *
 *   Browser.chrome
 *   Browser.safari
 *   Browser.opera
 *   Browser.msie
 *   Browser.edge
 *   Browser.edgeChromium()
 *   Browser.chromium()
 *   Browser.mozilla
 *   Browser.ipad
 *
 * The Browser object also populates Browser.version from the user-agent, along
 * with other incidental information e.g. Browser.webkit, Browser.trident
 */
function extract_browser_info_from_user_agent(userAgent) {
    const ua = userAgent.toLowerCase();
    const match = /(ipad)/.exec(ua) ||
        /(crios)[ \/]([\w.]+)/.exec(ua) ||
        /(fxios)[ \/]([\w.]+)/.exec(ua) ||
        /(opt|opr|opios).*[ \/]([\w.]+)/.exec(ua) ||
        /(edge)[ \/]([\w.]+)/.exec(ua) ||
        /(edg)[ \/]([\w.]+)/.exec(ua) ||
        /(chrome)[ \/]([\w.]+)/.exec(ua) ||
        /(webkit)[ \/]([\w.]+)/.exec(ua) ||
        /(opera)(?:.*version|)[ \/]([\w.]+)/.exec(ua) ||
        /(msie) ([\w.]+)/.exec(ua) ||
        /(trident).*? rv:([\w.]+)/.exec(ua) ||
        (ua.indexOf('compatible') < 0 && /(mozilla)(?:.*? rv:([\w.]+)|)/.exec(ua)) ||
        [];
    const versionMatch = ua.match(/version\/([\d.]+)/i);
    // Look for version in the form: "Version/X.x" i.e in Safari
    let version = versionMatch != null ? match[1] : null;
    if (version === 'webkit') {
        version = versionMatch != null ? versionMatch[1] : null;
    }
    return {
        browser: match[1] || '',
        version: version || match[2] || '0',
        userAgent,
    };
}
export function parseWindowsOsInfo(userAgent, isWindows) {
    const windowsME = /win(dows)? 9x 4\.90/.test(userAgent) || /windows me/.test(userAgent);
    const windowsOSInfo = {
        windows3_11: /win16/.test(userAgent),
        windows95: /windows 95/.test(userAgent) || /win95/.test(userAgent) || /windows_95/.test(userAgent),
        windows98: !windowsME && (/windows 98/.test(userAgent) || /win98/.test(userAgent)),
        windowsCE: /windows ce/.test(userAgent),
        windowsME,
        windowsNT4_0: /windows nt 4\.0/.test(userAgent) ||
            /winnt4.0/.test(userAgent) ||
            /winnt/.test(userAgent) ||
            /windows nt([^ ]| [^0-9])/.test(userAgent),
        windows2000: /windows nt 5\.0/.test(userAgent) || /windows 2000/.test(userAgent),
        windowsXP: /windows nt 5\.1/.test(userAgent) || /windows xp/.test(userAgent),
        windowsXPx64: /windows nt 5\.2/.test(userAgent),
        windowsVista: /windows nt 6\.0/.test(userAgent) || /windows vista/.test(userAgent),
        windows7: /windows nt 6\.1/.test(userAgent),
        windows8: /windows nt 6\.2/.test(userAgent),
        windows8_1: /windows nt 6\.3/.test(userAgent),
        windows10: /windows nt 6\.4/.test(userAgent) || /windows nt 10\.0/.test(userAgent),
        win7OrLower: false,
        win8OrHigher: false,
    };
    windowsOSInfo.win7OrLower =
        windowsOSInfo.windows3_11 ||
            windowsOSInfo.windows95 ||
            windowsOSInfo.windows98 ||
            windowsOSInfo.windowsCE ||
            windowsOSInfo.windowsME ||
            windowsOSInfo.windowsNT4_0 ||
            windowsOSInfo.windows2000 ||
            windowsOSInfo.windowsXP ||
            windowsOSInfo.windowsXPx64 ||
            windowsOSInfo.windowsVista ||
            windowsOSInfo.windows7;
    windowsOSInfo.win8OrHigher = isWindows && !windowsOSInfo.win7OrLower;
    return windowsOSInfo;
}
export function get_browser_info(userAgent = navigator.userAgent) {
    const browser_info = extract_browser_info_from_user_agent(userAgent);
    if (browser_info.browser === 'trident') {
        browser_info.browser = 'msie';
    }
    return browser_info;
}
export function get_plugins(plugins = navigator.plugins || []) {
    return Array.from(plugins);
}
export function getBrowserConstants(inputUserAgent = navigator.userAgent) {
    const browser_info = get_browser_info(inputUserAgent);
    const ipad = browser_info.browser === 'ipad' ? true : undefined;
    // The user agent for Chrome and Safari is the same on iOS with the distinction of the inclusion of CriOS on Chrome.
    // https://chromium.googlesource.com/chromium/src/+/master/docs/ios/user_agent.md
    const chrome = ['chrome', 'crios'].includes(browser_info.browser) ? true : undefined;
    // Webkit is matched after Chrome and CriOS, so this must be Safari.
    const safari = browser_info.browser === 'webkit' || Boolean(ipad) ? true : undefined;
    const opera = ['opt', 'opr', 'opios', 'opera'].includes(browser_info.browser) ? true : undefined;
    const msie = browser_info.browser === 'msie' ? true : undefined;
    const edge = browser_info.browser === 'edge' ||
        browser_info.browser === 'edg' ||
        browser_info.browser === 'edgios'
        ? true
        : undefined;
    const edgeChromium = () => browser_info.browser === 'edg';
    const chromium = () => Boolean(chrome) || edgeChromium();
    // The user agent for Firefox on iOS contains FxiOS.
    const mozilla = ['fxios', 'mozilla'].includes(browser_info.browser)
        ? true
        : undefined;
    const webkit = browser_info.browser === 'webkit' || Boolean(ipad) || Boolean(chrome) ? true : undefined;
    const mac = /(macintosh)/.test(navigator.userAgent.toLowerCase());
    const windows = /win(16|nt|dows)/.test(navigator.userAgent.toLowerCase());
    const windowsInfo = parseWindowsOsInfo(navigator.userAgent.toLowerCase(), windows);
    const iphone = /(iphone)/.test(navigator.userAgent.toLowerCase());
    const ipod = /(ipod)/.test(navigator.userAgent.toLowerCase());
    const iOS = ipad || ipod || iphone;
    const facebookWebView = /(FBAN)|(FBAV)/.test(navigator.userAgent);
    const browser_name = browser_info.browser;
    const version = browser_info.version;
    const userAgent = browser_info.userAgent;
    const plugins = get_plugins();
    const pluginNames = plugins.map((plugin) => plugin.name);
    return {
        browser_info,
        ipad,
        chrome,
        safari,
        opera,
        msie,
        edge,
        edgeChromium,
        chromium,
        mozilla,
        webkit,
        mac,
        windows,
        windowsInfo,
        iphone,
        ipod,
        iOS,
        facebookWebView,
        browser_name,
        version,
        userAgent,
        plugins,
        pluginNames,
    };
}
// Define our local exports, to satisfy existing consumers.
export const { ipad, chrome, safari, opera, msie, edge, edgeChromium, chromium, mozilla, webkit, mac, windows, windowsInfo, iphone, ipod, iOS, facebookWebView, browser_name, version, userAgent, plugins, pluginNames, } = getBrowserConstants();
export function getBrowserShortName() {
    if (chrome) {
        return 'chrome';
    }
    else if (edge) {
        return 'edge';
    }
    else if (mozilla) {
        return 'firefox';
    }
    else if (msie) {
        return 'msie';
    }
    else if (safari) {
        return 'safari';
    }
    return 'other';
}
export function checkBrowserVersion(browser, v, greaterThan = true) {
    if (greaterThan) {
        return Boolean(browser) && parseInt(version, 10) >= v;
    }
    return Boolean(browser) && parseInt(version, 10) <= v;
}
/**
 * Returns true if the browser is a supported mobile browser, false otherwise.
 *
 * NOTE: The Logic for 'googlebot-mobile' and 'microsoft office mobile' was put in place
 * to keep the logic symmetric to the server side implementation. However a cursory google search
 * did not return any known user agent strings that match these substrings.
 *
 * @param {string} userAgent - the user agent string of the client browser
 */
export function is_supported_mobile_browser(userAgent = navigator.userAgent) {
    const ua = (userAgent || '').toLowerCase();
    return (ipod ||
        iphone ||
        ua.indexOf('tizen') !== -1 ||
        (ua.indexOf('android') !== -1 && ua.indexOf('mobile') !== -1) ||
        ua.indexOf('bb10') !== -1 ||
        ua.indexOf('netfront') !== -1 ||
        /windows phone|opera mini|blackberry|nokia/.test(ua) ||
        (ua.indexOf('firefox') !== -1 && ua.indexOf('mobile') !== -1) ||
        ua.indexOf('googlebot-mobile') !== -1 ||
        ua.indexOf('iemobile') !== -1 ||
        ua.indexOf('opera mobi') !== -1 ||
        /ucweb|ucbrowser/.test(ua) ||
        ua.indexOf('microsoft office mobile') !== -1);
}
/**
 * Returns true if the browser is an android browser, false otherwise.
 *
 * @param {string} userAgent - the user agent string of the client browser
 */
export function is_android(userAgent = navigator.userAgent) {
    const ua = (userAgent || '').toLowerCase();
    // NOTE: 'silk' is the kindle fire browser
    return ua.indexOf('android') !== -1 || ua.indexOf('silk') !== -1;
}
/**
 * Returns true if the browser is a tablet browser, false otherwise.
 *
 * @param {string} userAgent - the user agent string of the client browser
 */
export function is_tablet(userAgent = navigator.userAgent) {
    return ipad || (is_android(userAgent) && !is_supported_mobile_browser(userAgent));
}
/**
 * Returns true if the browser is a mobile or tablet browser, false otherwise.
 *
 * @param {string} userAgent - the user agent string of the client browser
 */
export function is_mobile_or_tablet(userAgent = navigator.userAgent) {
    return is_supported_mobile_browser(userAgent) || is_tablet(userAgent);
}
// TODO (drg): this function looks upload related and probably doesn't belong in this module
/**
 * Returns true if the browser supports the folder picker variant of the file picker type of
 * input element, <input type="file" webkitdirectory .../>
 *
 * List of supporting browsers: https://caniuse.com/#feat=input-file-directory
 */
export function is_input_webkitdirectory_supported() {
    const tmpInput = document.createElement('input');
    // This check has begun returning true on iOS even though iOS does not support uploading folders
    return 'webkitdirectory' in tmpInput && !iOS;
}
// Note: this is not a browser attribute per se but this is a good home for a widely use constant.
// Prior to Edge moving over to using Chromium internally in 2020 it did not support custom mime-types,
// however since Chromium has always supported these, and we don't support msie, this can now just be
// a constant string. Leaving it here in case we ever need to add back conditionals for different browsers.
export const INTERNAL_FILE_DATA_TRANSFER_TYPE = 'application/x-dropbox-file-list';
//# sourceMappingURL=browser_detection.preenvesnextjs.map
